import * as THREE from 'three';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {
    DUPLICATE_PLACEMENT_GAP,
    DUPLICATE_FLOOR_ROW_Z_OFFSET,
    DUPLICATE_FLOOR_WALL_MARGIN,
    DUPLICATE_FLOOR_BOUNDS_PADDING,
    getDuplicateFloorPlacementBounds,
} from 'components/customer/RoomPlanner/helpers/cabinetPlacement';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {isHangingLayoutItem} from 'components/customer/RoomPlanner/helpers/placeholder/style';

export {isHangingLayoutItem};

const BACK_WALL_Z_PADDING = 12.46;
const OVERLAP_ITERATIONS = 40;

type SpawnBounds = {
    minX: number;
    maxX: number;
    backWallZ: number;
};

/**
 * Collision AABB used for spawn overlap. Placeholders use mesh-only bounds
 * so overlay children do not inflate the footprint.
 * @param {CabinetMesh} item Cabinet or placeholder
 * @return {THREE.Box3} World-space bounds
 */
export const getPlaceholderSpawnCollisionBox = (
    item: CabinetMesh
): THREE.Box3 => {
    if (item.userData?.isPlaceholder) {
        const box = computeWorldBoundsExcludingAnimatedDoorParts(item);
        if (
            !box.isEmpty() &&
            Number.isFinite(box.min.x) &&
            Number.isFinite(box.max.x)
        ) {
            return box;
        }
    }
    return new THREE.Box3().setFromObject(item);
};

const isSameLayoutItem = (a: CabinetMesh, b: CabinetMesh): boolean => {
    if (a === b || a.uuid === b.uuid) return true;
    return (
        a.userData?.id === b.userData?.id &&
        Boolean(a.userData?.isPlaceholder) ===
            Boolean(b.userData?.isPlaceholder)
    );
};

const boundingBoxesOverlapXZ = (
    boxA: THREE.Box3,
    boxB: THREE.Box3,
    epsilon = DUPLICATE_PLACEMENT_GAP
): boolean =>
    boxA.min.x < boxB.max.x - epsilon &&
    boxA.max.x > boxB.min.x + epsilon &&
    boxA.min.z < boxB.max.z - epsilon &&
    boxA.max.z > boxB.min.z + epsilon;

/**
 * Strict 3D overlap, including height, so a tall fridge cannot sit inside a
 * wall cabinet and two placeholders cannot share the same volume.
 * @param {THREE.Box3} boxA First bounds
 * @param {THREE.Box3} boxB Second bounds
 * @param {number} [epsilon] Minimum gap treated as touching, not overlapping
 * @return {boolean}
 */
export const boundingBoxesOverlap3D = (
    boxA: THREE.Box3,
    boxB: THREE.Box3,
    epsilon = DUPLICATE_PLACEMENT_GAP
): boolean =>
    boundingBoxesOverlapXZ(boxA, boxB, epsilon) &&
    boxA.min.y < boxB.max.y - epsilon &&
    boxA.max.y > boxB.min.y + epsilon;

const getNeighbors = (
    group: CabinetMesh,
    existingItems: CabinetMesh[]
): CabinetMesh[] =>
    existingItems.filter(
        (item) => !item.userData?.isTempMesh && !isSameLayoutItem(item, group)
    );

const getSpawnBounds = (): SpawnBounds => {
    const dimensions = getRoomDimensionsComputed();
    const placementBounds = getDuplicateFloorPlacementBounds(Camera.scene);
    const pad = DUPLICATE_FLOOR_BOUNDS_PADDING;
    return {
        minX: placementBounds
            ? placementBounds.min.x + pad
            : -dimensions.roomWidth / 2 + DUPLICATE_FLOOR_WALL_MARGIN,
        maxX: placementBounds
            ? placementBounds.max.x - pad
            : dimensions.roomWidth / 2 - DUPLICATE_FLOOR_WALL_MARGIN,
        backWallZ: dimensions.roomDepthComputed + BACK_WALL_Z_PADDING,
    };
};

const getPivotOffsets = (group: CabinetMesh) => {
    group.updateMatrixWorld(true);
    const ownBox = getPlaceholderSpawnCollisionBox(group);
    return {
        offsetToMinX: group.position.x - ownBox.min.x,
        offsetToMinZ: group.position.z - ownBox.min.z,
        ownDepth: Math.max(ownBox.max.z - ownBox.min.z, 1),
    };
};

const placeByMinEdge = (
    group: CabinetMesh,
    minX: number,
    rowBackZ: number,
    offsetToMinX: number,
    offsetToMinZ: number
): void => {
    group.position.x = minX + offsetToMinX;
    group.position.z = rowBackZ + offsetToMinZ;
    group.updateMatrixWorld(true);
};

const findOverlapping = (
    group: CabinetMesh,
    neighbors: CabinetMesh[],
    test: (ownBox: THREE.Box3, otherBox: THREE.Box3) => boolean
): CabinetMesh[] => {
    const ownBox = getPlaceholderSpawnCollisionBox(group);
    return neighbors.filter((item) =>
        test(ownBox, getPlaceholderSpawnCollisionBox(item))
    );
};

const rightmostMaxX = (items: CabinetMesh[]): number => {
    let maxX = -Infinity;
    for (const item of items) {
        const box = getPlaceholderSpawnCollisionBox(item);
        if (box.max.x > maxX) maxX = box.max.x;
    }
    return maxX;
};

const resolveOverlaps = (
    group: CabinetMesh,
    neighbors: CabinetMesh[],
    bounds: SpawnBounds,
    offsets: {offsetToMinX: number; offsetToMinZ: number; ownDepth: number},
    test: (ownBox: THREE.Box3, otherBox: THREE.Box3) => boolean
): void => {
    const rowStep =
        offsets.ownDepth > 0
            ? offsets.ownDepth + DUPLICATE_PLACEMENT_GAP
            : DUPLICATE_FLOOR_ROW_Z_OFFSET;

    for (let i = 0; i < OVERLAP_ITERATIONS; i++) {
        const ownBox = getPlaceholderSpawnCollisionBox(group);
        const rowBackZ = group.position.z - offsets.offsetToMinZ;

        if (ownBox.max.x > bounds.maxX) {
            placeByMinEdge(
                group,
                bounds.minX + 0.5,
                rowBackZ + rowStep,
                offsets.offsetToMinX,
                offsets.offsetToMinZ
            );
            continue;
        }

        const overlapping = findOverlapping(group, neighbors, test);
        if (overlapping.length === 0) return;

        const pushTo = rightmostMaxX(overlapping);
        placeByMinEdge(
            group,
            pushTo + DUPLICATE_PLACEMENT_GAP,
            rowBackZ,
            offsets.offsetToMinX,
            offsets.offsetToMinZ
        );
    }
};

/**
 * Pack a new placeholder on the back wall, to the right of same-height-class
 * cabinets and placeholders (floor vs hanging), then wrap to a new row.
 * @param {CabinetMesh} group Newly loaded placeholder
 * @param {CabinetMesh[]} existingItems Cabinets and placeholders already in the room
 * @return {THREE.Vector3} Final world position
 */
export const determinePlaceholderSpawnLocation = (
    group: CabinetMesh,
    existingItems: CabinetMesh[]
): THREE.Vector3 => {
    const bounds = getSpawnBounds();
    const offsets = getPivotOffsets(group);
    const hanging = isHangingLayoutItem(group);
    const packingNeighbors = getNeighbors(group, existingItems).filter(
        (item) => isHangingLayoutItem(item) === hanging
    );

    placeByMinEdge(
        group,
        bounds.minX + 0.5,
        bounds.backWallZ,
        offsets.offsetToMinX,
        offsets.offsetToMinZ
    );

    resolveOverlaps(
        group,
        packingNeighbors,
        bounds,
        offsets,
        boundingBoxesOverlapXZ
    );

    group.userData.savedPosition = group.position.clone();
    return group.position.clone();
};

/**
 * Nudge / wrap a placeholder until its 3D volume no longer intersects any
 * cabinet or other placeholder. Height is included so tall units cannot sit
 * inside wall cabinets, and two appliances cannot occupy the same space.
 * @param {CabinetMesh} group Placeholder that was just spawned or duplicated
 * @param {CabinetMesh[]} existingItems Cabinets and placeholders already in the room
 * @return {THREE.Vector3} Final world position
 */
export const resolvePlaceholderSpawnOverlaps = (
    group: CabinetMesh,
    existingItems: CabinetMesh[]
): THREE.Vector3 => {
    const bounds = getSpawnBounds();
    const offsets = getPivotOffsets(group);
    resolveOverlaps(
        group,
        getNeighbors(group, existingItems),
        bounds,
        offsets,
        boundingBoxesOverlap3D
    );
    group.userData.savedPosition = group.position.clone();
    return group.position.clone();
};
